import React, { useState, useEffect, useCallback } from "react";
import { Card, Form, Button, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import RichTextEditor from "components/common/RichTextEditor";
import { useUserManagement } from "hooks";
import useDebounce from "hooks/route-rules/useDebounce";
import paths from "routes/paths";
import useTasks from "hooks/projects/useTasks";

const TaskFormPage = () => {
  const navigate = useNavigate();
  const { projectId, iterationId, taskId } = useParams();
  const { tasksByIteration, createTask, updateTask, fetchTasksByIteration } = useTasks(projectId, iterationId);
  const tasks = tasksByIteration[iterationId] || [];
  const { users, loading: usersLoading } = useUserManagement();
  const [isLoading, setIsLoading] = useState(!!taskId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const debouncedUserSearch = useDebounce(userSearchTerm, 300);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    acceptanceCriteria: "",
    status: "OPEN",
    priority: 2,
    points: 1,
    assignedUsers: [],
    startedAt: "",
    expectedCompletionDate: "",
    links: [],
  });

  const isEditMode = !!taskId;

  const priorityOptions = [
    { value: 0, label: "Highest Priority (0)" },
    { value: 1, label: "High Priority (1)" },
    { value: 2, label: "Medium Priority (2)" },
    { value: 3, label: "Low Priority (3)" },
    { value: 4, label: "Lowest Priority (4)" },
  ];

  const pointsOptions = [1, 2, 3, 5, 8];

  // Load task data if in edit mode (only once)
  useEffect(() => {
    const loadTaskData = async () => {
      if (isEditMode && !hasLoadedInitialData) {
        try {
          // Always fetch tasks first to ensure we have the latest data
          await fetchTasksByIteration(projectId, iterationId);

          // Give state a moment to update
          await new Promise(resolve => setTimeout(resolve, 200));

          // Now check if we have the task
          const currentTasks = tasksByIteration[iterationId] || [];
          const task = currentTasks.find((t) => t.id === taskId);

          if (task) {
            // Handle assigned users - API returns assigneeName, we need to find the matching user
            let assignedUsers = [];
            if (task.assigneeName) {
              try {
                // Search for the user by name to get their full user object
                const userResults = await searchUserProfiles(task.assigneeName);
                const matchingUser = userResults.find(user =>
                  `${user.firstName} ${user.lastName}` === task.assigneeName ||
                  user.email === task.assigneeName
                );

                if (matchingUser) {
                  assignedUsers = [{
                    value: matchingUser.id,
                    label: `${matchingUser.firstName} ${matchingUser.lastName} (${matchingUser.email})`,
                  }];
                } else {
                  // If user not found, create a placeholder (this shouldn't happen in normal cases)
                  console.warn(`User "${task.assigneeName}" not found in user database`);
                  assignedUsers = [{
                    value: task.assigneeName,
                    label: task.assigneeName,
                  }];
                }
              } catch (error) {
                console.error("Error searching for assignee:", error);
                // Fallback: create placeholder
                assignedUsers = [{
                  value: task.assigneeName,
                  label: task.assigneeName,
                }];
              }
            }

            const newFormData = {
              title: task.title || "",
              description: task.description || "",
              acceptanceCriteria: task.acceptanceCriteria || "",
              status: task.status || "OPEN",
              priority: task.priority !== undefined ? task.priority : 2,
              points: task.points !== undefined ? task.points : 1,
              assignedUsers: assignedUsers,
              startedAt: task.startedAt ? task.startedAt.split('T')[0] : "",
              expectedCompletionDate: task.expectedCompletionDate ? task.expectedCompletionDate.split('T')[0] : "",
              links: Array.isArray(task.links) ? task.links : [],
            };

            setFormData(newFormData);
            setHasLoadedInitialData(true);
            setIsLoading(false);
          } 
          } catch (error) {
          console.error("Failed to load task data:", error);
          toast.error("Failed to load task data");
            setIsLoading(false);
        }
      } else if (!isEditMode) {
        setIsLoading(false);
      }
    };

    loadTaskData();
  }, [taskId, isEditMode, hasLoadedInitialData, fetchTasksByIteration, projectId, iterationId, tasksByIteration]);

  // Use the backend search from useUserManagement hook
  const { searchUserProfiles } = useUserManagement();

  // Search users when debounced search term changes (like users table)
  useEffect(() => {
    const searchUsers = async () => {
      if (debouncedUserSearch.trim()) {
        setIsSearchingUsers(true);
        try {
          const results = await searchUserProfiles(debouncedUserSearch.trim());
          setSearchedUsers(results);
        } catch (error) {
          console.error("Error searching users:", error);
          setSearchedUsers([]);
        } finally {
          setIsSearchingUsers(false);
        }
      } else {
        setSearchedUsers([]);
      }
    };

    searchUsers();
  }, [debouncedUserSearch, searchUserProfiles]);

  // Add user to assigned users
  const addUserToTask = (user) => {
    const isAlreadyAssigned = formData.assignedUsers.some(u => u.value === user.id);
    if (!isAlreadyAssigned) {
      setFormData(prev => ({
        ...prev,
        assignedUsers: [...prev.assignedUsers, {
          value: user.id,
          label: `${user.firstName} ${user.lastName} (${user.email})`
        }]
      }));
      setUserSearchTerm(""); // Clear search after adding
      setSearchedUsers([]); // Clear results
    }
  };

  // Remove user from assigned users
  const removeUserFromTask = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedUsers: prev.assignedUsers.filter(u => u.value !== userId)
    }));
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    if (!iterationId) {
      toast.error("Iteration ID is required");
      return;
    }

    if (!projectId) {
      toast.error("Project ID is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const taskData = {
        type: formData.type || "TASK",
        title: formData.title.trim(),
        description: formData.description || null,
        acceptanceCriteria: formData.acceptanceCriteria || null,
        status: formData.status,
        priority: formData.priority,
        points: formData.points,
        iterationId: iterationId,
        startedAt: formData.startedAt || null,
        expectedCompletionDate: formData.expectedCompletionDate || null,
        actualCompletionDate: null,
        comment: "",
        links: formData.links || [],
        // API expects assigneeName as string, not assignedUserIds array
        ...(formData.assignedUsers && formData.assignedUsers.length > 0 && {
          assigneeName: formData.assignedUsers[0].label.split(' (')[0], // Extract name from "Name (email)" format
        }),
      };

      if (isEditMode) {
        await updateTask(projectId, taskId, taskData);
        toast.success("Task updated successfully");
        // Dispatch event to refresh tasks in other components
        window.dispatchEvent(new CustomEvent("taskUpdated", {
          detail: { projectId, iterationId, taskId }
        }));
      } else {
        await createTask(projectId, taskData);
        toast.success("Task created successfully");
      }

      navigate(paths.projectIterationDetails(projectId, iterationId));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 
        `Failed to ${isEditMode ? "update" : "create"} task`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="tasks"
          title={isEditMode ? "Edit Task" : "Create New Task"}
          subtitle={isEditMode ? "Update task details" : "Create a new task for the selected iteration"}
          className="mb-3 mt-3 flex-1"
        />

        <Button
          variant="info"
          size="sm"
          className="text-sm"
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </div>

      <Card className="custom-shadow">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {/* Show loading state for edit mode */}
            {isLoading && isEditMode && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2 text-muted">Loading task data...</div>
              </div>
            )}

            {/* Show form only when not loading or not in edit mode */}
            {(!isLoading || !isEditMode) && (
              <>
            <Form.Group className="mb-4">
              <Form.Label>Task Title</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="Enter task title"
                required
                disabled={isSubmitting}
                autoComplete="off"
              />
            </Form.Group>

            <RichTextEditor
              label="Description"
              value={formData.description}
              onChange={(html) => handleChange("description", html)}
              className="mb-4"
            />

            <Form.Group className="mb-4 mt-6">
              <Form.Label>Acceptance Criteria</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.acceptanceCriteria}
                onChange={(e) => handleChange("acceptanceCriteria", e.target.value)}
                placeholder="Define the acceptance criteria for this task"
                disabled={isSubmitting}
              />
            </Form.Group>

            <Row className="mt-4">
              <Col md={6} className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={formData.priority}
                  onChange={(e) =>
                    handleChange("priority", parseInt(e.target.value))
                  }
                  required
                  disabled={isSubmitting}
                >
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={4} className="mb-3">
                <Form.Label>Points (Days)</Form.Label>
                <Form.Select
                  value={formData.points}
                  onChange={(e) =>
                    handleChange("points", parseInt(e.target.value))
                  }
                  required
                  disabled={isSubmitting}
                >
                  {pointsOptions.map((point) => (
                    <option key={point} value={point}>
                      {point} {point === 1 ? "day" : "days"}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted d-block">
                  Each point represents 1 day of work
                </Form.Text>
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>Started At</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.startedAt}
                  onChange={(e) => handleChange("startedAt", e.target.value)}
                  disabled={isSubmitting}
                />
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>Expected Completion Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.expectedCompletionDate}
                  onChange={(e) => handleChange("expectedCompletionDate", e.target.value)}
                  disabled={isSubmitting}
                />
              </Col>
            </Row>

            <Form.Group className="mb-4 mt-3">
              <Form.Label>Assign Users</Form.Label>

              {/* Search Input - Like users table */}
              <InputGroup className="mb-2">
                <InputGroup.Text>
                  <FontAwesomeIcon icon="search" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  disabled={isSubmitting}
                />
                {isSearchingUsers && (
                  <InputGroup.Text>
                    <Spinner animation="border" size="sm" />
                  </InputGroup.Text>
                )}
              </InputGroup>

              {/* Assigned Users Display */}
              {formData.assignedUsers.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Assigned Users:</small>
                  <div className="d-flex flex-wrap gap-2">
                    {formData.assignedUsers.map((user) => (
                      <span
                        key={user.value}
                        className="badge bg-primary d-flex align-items-center"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {user.label}
                        <button
                          type="button"
                          className="btn btn-sm p-0 ms-2 text-white border-0 bg-transparent"
                          onClick={() => removeUserFromTask(user.value)}
                          disabled={isSubmitting}
                          style={{ fontSize: "0.6rem", lineHeight: 1 }}
                        >
                          <FontAwesomeIcon icon="times" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Results - Like users table results */}
              {userSearchTerm && (
                <div className="border rounded p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {isSearchingUsers ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <small className="text-muted d-block mt-1">Searching...</small>
                    </div>
                  ) : searchedUsers.length > 0 ? (
                    searchedUsers.slice(0, 10).map((user) => {
                      const isAlreadyAssigned = formData.assignedUsers.some(u => u.value === user.id);
                      return (
                        <div
                          key={user.id}
                          className={`d-flex align-items-center justify-content-between p-2 rounded ${
                            isAlreadyAssigned ? "bg-light" : "hover-bg-light"
                          }`}
                          style={{ cursor: isAlreadyAssigned ? "default" : "pointer" }}
                          onClick={() => !isAlreadyAssigned && addUserToTask(user)}
                        >
                          <div>
                            <div className="fw-medium" style={{ fontSize: "0.875rem" }}>
                              {user.firstName} {user.lastName}
                            </div>
                            <small className="text-muted">{user.email}</small>
                          </div>
                          {isAlreadyAssigned ? (
                            <small className="text-success fw-medium">Assigned</small>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={isSubmitting}
                              style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                            >
                              Add
                            </Button>
                          )}
                        </div>
                      );
                    })
                  ) : userSearchTerm.length >= 2 ? (
                    <div className="text-center py-3 text-muted">
                      <small>No users found</small>
                    </div>
                  ) : (
                    <div className="text-center py-3 text-muted">
                      <small>Type at least 2 characters to search</small>
                    </div>
                  )}
                </div>
              )}

              <Form.Text className="text-muted d-block">
                Search and assign users to this task
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mt-6">
              <Button
                variant="success"
                type="submit"
                size="sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  isEditMode ? "Update Task" : "Create Task"
                )}
              </Button>
            </div>
              </>
            )}
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default TaskFormPage;
